// European countries PriorPay operates in (EU-27 + United Kingdom, Switzerland,
// Norway), as ISO 3166-1 alpha-2 codes. Greece is GR here (its VAT code is EL).
export const EUROPEAN_COUNTRY_CODES = [
  'AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR', 'DE', 'GR', 'HU', 'IE',
  'IT', 'LV', 'LT', 'LU', 'MT', 'NL', 'PL', 'PT', 'RO', 'SK', 'SI', 'ES', 'SE',
  'GB', 'CH', 'NO',
] as const;

/** Country name in the given UI language (e.g. "ES" -> "España"), or the code itself. */
export function countryName(code: string, language: string): string {
  try {
    return new Intl.DisplayNames([language], { type: 'region' }).of(code.toUpperCase()) || code;
  } catch {
    return code;
  }
}

// Upper-case, accent-free country names (es/en/fr/pt) and alpha-3 codes -> alpha-2.
// Mirrors backend/app/country_utils.py: older events may hold free text ("España").
const COUNTRY_ALIASES: Record<string, string[]> = {
  AT: ['AUSTRIA', 'AUTRICHE', 'AUT'],
  BE: ['BELGICA', 'BELGIUM', 'BELGIQUE', 'BEL'],
  BG: ['BULGARIA', 'BULGARIE', 'BGR'],
  HR: ['CROACIA', 'CROATIA', 'CROATIE', 'HRV'],
  CY: ['CHIPRE', 'CYPRUS', 'CHYPRE', 'CYP'],
  CZ: ['REPUBLICA CHECA', 'CHEQUIA', 'CZECHIA', 'CZECH REPUBLIC', 'TCHEQUIE', 'CZE'],
  DK: ['DINAMARCA', 'DENMARK', 'DANEMARK', 'DNK'],
  EE: ['ESTONIA', 'ESTONIE', 'EST'],
  FI: ['FINLANDIA', 'FINLAND', 'FINLANDE', 'FIN'],
  FR: ['FRANCIA', 'FRANCE', 'FRANCA', 'FRA'],
  DE: ['ALEMANIA', 'GERMANY', 'ALLEMAGNE', 'ALEMANHA', 'DEU'],
  GR: ['GRECIA', 'GREECE', 'GRECE', 'EL', 'GRC'],
  HU: ['HUNGRIA', 'HUNGARY', 'HONGRIE', 'HUN'],
  IE: ['IRLANDA', 'IRELAND', 'IRLANDE', 'IRL'],
  IT: ['ITALIA', 'ITALY', 'ITALIE', 'ITA'],
  LV: ['LETONIA', 'LATVIA', 'LETTONIE', 'LVA'],
  LT: ['LITUANIA', 'LITHUANIA', 'LITUANIE', 'LTU'],
  LU: ['LUXEMBURGO', 'LUXEMBOURG', 'LUX'],
  MT: ['MALTA', 'MALTE', 'MLT'],
  NL: ['PAISES BAJOS', 'HOLANDA', 'NETHERLANDS', 'PAYS-BAS', 'PAISES BAIXOS', 'NLD'],
  PL: ['POLONIA', 'POLAND', 'POLOGNE', 'POL'],
  PT: ['PORTUGAL', 'PRT'],
  RO: ['RUMANIA', 'ROMANIA', 'ROUMANIE', 'ROU'],
  SK: ['ESLOVAQUIA', 'SLOVAKIA', 'SLOVAQUIE', 'SVK'],
  SI: ['ESLOVENIA', 'SLOVENIA', 'SLOVENIE', 'SVN'],
  ES: ['ESPANA', 'SPAIN', 'ESPAGNE', 'ESPANHA', 'ESP'],
  SE: ['SUECIA', 'SWEDEN', 'SUEDE', 'SWE'],
  GB: ['REINO UNIDO', 'UNITED KINGDOM', 'UK', 'ROYAUME-UNI', 'INGLATERRA', 'ENGLAND', 'GBR'],
  CH: ['SUIZA', 'SWITZERLAND', 'SUISSE', 'SUICA', 'CHE'],
  NO: ['NORUEGA', 'NORWAY', 'NORVEGE', 'NOR'],
};

const ALIAS_TO_CODE: Record<string, string> = Object.fromEntries(
  Object.entries(COUNTRY_ALIASES).flatMap(([code, aliases]) => aliases.map((alias) => [alias, code])),
);

/**
 * ISO alpha-2 code for an event's country field, or '' if it can't be resolved.
 * Accepts "es", "ESP" or a name in es/en/fr/pt ("España", "Spain"); same rules
 * as normalize_country_code in the backend.
 */
export function normalizeCountryCode(value: string | null | undefined): string {
  if (!value) return '';
  const plain = value.trim().normalize('NFD').replace(/[̀-ͯ]/g, '').toUpperCase();
  if (ALIAS_TO_CODE[plain]) return ALIAS_TO_CODE[plain];
  return /^[A-Z]{2}$/.test(plain) ? plain : '';
}

/** Default tax rate of an event's country in one rate table (0 if unknown). */
export function countryTaxRateFor(rates: Record<string, number>, country: string | null | undefined): number {
  const code = normalizeCountryCode(country);
  return (code && rates[code]) || 0;
}
